import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { fileActions, httpRequestsOnSuccessActions, tagActions } from 'store';
import { FindSuccessItem, Images, PermissionList, RolePermission, sortListByKey, tagEnums, useModal } from 'utils';
import { FullTable } from '../../tabs';
import { documentActions, fileNotYet, filesBody, filesHead } from './constants';
import { CustomDelete } from '../../modal';
import { AddCircleBlue, TabSwitcher } from '../../buttons';
import { UploadFiles } from './uploadFiles';
import { SelectActions } from '../selectActions';
import { fileTabEnums, fileTabTypes } from './constants';
import { ShareFile } from './shareFile';

const GET_ACTION_TYPE = 'GET_FILES';
const DELETE_ACTION_TYPE = 'DELETE_FILES';
const ARCHIVE_ACTION_TYPE = 'ARCHIVE_FILES';

export const FilesComponent = ({
    currentId,
    onResource,
    permissions,
    height,
    noSearch,
    margin,
    uploadBtnText,
    uploadBtnIcon,
    shareBtn,
    withExpiration, // client documents: expiration date on upload/edit + "Expires" column
}) => {
    const params = useParams();
    const resourceId = currentId ? currentId : params?.id;
    const filesList = useSelector((state) => state.files.files);
    const dispatch = useDispatch();
    const checkPermissions = permissions ? RolePermission([...permissions]) : '';
    const { open } = useModal();
    const [selectedId, setSelectedId] = useState('');
    const [selectedList, setSelectedList] = useState([]);
    const [selectedTab, setSelectedTab] = useState(fileTabEnums.ACTIVE);
    const archived = selectedTab === fileTabEnums.ARCHIVED;
    const archiveSuccess = FindSuccessItem(ARCHIVE_ACTION_TYPE);
    const reqParams = {
        model: onResource,
        id: resourceId,
        reqParams: { archived: archived },
    };

    useEffect(() => {
        dispatch(tagActions.getTags({ module: tagEnums.DOCUMENT }));
    }, []);

    useEffect(() => {
        dispatch(fileActions.getFiles(reqParams));
    }, [selectedTab]);

    useEffect(() => {
        if (archiveSuccess) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ARCHIVE_ACTION_TYPE));
            setSelectedList([]);
        }
    }, [archiveSuccess]);

    const handleDownload = (item) => {
        const fileType = item?.file?.originalName
            ? item?.file?.originalName
            : item?.file?.format === 'svg+xml'
            ? 'file.svg'
            : `file.${item?.file?.format}`;

        if (!item?.file?.url) return;
        setSelectedId({ action: documentActions.download, id: item?.id });
        axios({
            url: item.file.url,
            method: 'GET',
            responseType: 'blob',
            useBaseUrl: false,
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileType); //or any other extension
                document.body.appendChild(link);
                link.click();
                setSelectedId('');
            })
            .catch(() => {
                setSelectedId('');
            });
    };

    const removeSelectedList = () => {
        setSelectedList([]);
    };
    const handleClickActions = (item, name) => {
        const isMultiple = name === 'deleteMultiple';

        if (name === 'delete' || name === 'deleteMultiple') {
            open(
                <CustomDelete
                    info={`Are you sure you want to delete ${isMultiple ? 'these files' : 'this file'}?`}
                    text={`Once deleted, ${isMultiple ? 'these files' : 'this file'} will be permanently lost.`}
                    handleDel={() =>
                        dispatch(
                            fileActions.deleteFiles(reqParams, {
                                docIds: isMultiple ? [...selectedList] : [item?.id],
                            })
                        )
                    }
                    afterSuccess={removeSelectedList}
                    actionType={DELETE_ACTION_TYPE}
                    body={
                        <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                            <img style={{ marginTop: '3px' }} src={Images.uploadedFile} alt="icon" />
                            <p style={{ fontWeight: 600 }}>{isMultiple ? `${selectedList.length} files selected` : item?.name}</p>
                        </div>
                    }
                />
            );
        } else if (name === 'download') {
            handleDownload(item);
        } else if (name === 'edit') {
            open(
                <UploadFiles
                    defaultValue={item}
                    currentId={resourceId}
                    onResource={onResource}
                    selectedTab={selectedTab}
                    withExpiration={withExpiration}
                />
            );
        } else if (name === 'share') {
            open(<ShareFile item={item} reqParams={reqParams} />);
        } else if (name === 'unShare') {
            open(<ShareFile item={item} reqParams={reqParams} unShare={true} />);
        } else if (name === 'archiveAnarchive') {
            archiveAnArchive(item);
        }
    };

    const handleSelect = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (name === 'all') {
            let allList = filesList?.length === selectedList?.length ? [] : filesList?.map((i) => i?.id);
            setSelectedList(allList);
        } else {
            const list = [...selectedList];
            const uniqSet = new Set(list);

            if (!uniqSet.has(item?.id)) {
                list.push(item?.id);
            } else {
                list.indexOf(item?.id) !== -1 && list.splice(list.indexOf(item?.id), 1);
            }
            setSelectedList(list);
        }
    };

    const archiveAnArchive = (item) => {
        if (item) {
            setSelectedId({ action: ARCHIVE_ACTION_TYPE, id: item?.id });
        }
        dispatch(
            fileActions.archiveFiles(
                {
                    model: onResource,
                    id: resourceId,
                    reqParams: { archived: archived || false },
                },
                {
                    docId: item ? [item?.id] : selectedList,
                    archived: !archived,
                }
            )
        );
    };

    return (
        <div>
            {checkPermissions && !!selectedList?.length && (
                <SelectActions
                    submitText={archived ? 'Unarchive Files' : 'Archive Files'}
                    submitBtnIcon={archived ? Images.archive : Images.unarchive}
                    selectInnetText={selectedList?.length === filesList?.length ? 'Clear Selection' : ` Select all`}
                    actionType={ARCHIVE_ACTION_TYPE}
                    removeSelected={removeSelectedList}
                    selectedList={selectedList?.length}
                    afterSuccess={removeSelectedList}
                    selectAll={() => {
                        if (selectedList?.length === filesList?.length) {
                            removeSelectedList();
                        } else {
                            setSelectedList(filesList?.map((i) => i?.id));
                        }
                    }}
                    submitClaims={() => archiveAnArchive()}
                    renderOtherBtns={
                        RolePermission([PermissionList.APPT_LOCK?.code]) && (
                            <div className="flex-align-center" style={{ height: 30, gap: 8 }}>
                                <button
                                    onClick={() => {
                                        handleClickActions({}, 'deleteMultiple');
                                    }}
                                    className="selector-action-btn">
                                    <img src={Images.redTrash} alt="lock" />
                                </button>
                            </div>
                        )
                    }
                />
            )}

            <div className="space-between-align-center" style={{ margin: '-10px 0 16px' }}>
                <TabSwitcher
                    setTab={(tab) => {
                        setSelectedTab(tab);
                        removeSelectedList();
                    }}
                    tab={selectedTab}
                    list={fileTabTypes}
                    defaultType={fileTabEnums.ACTIVE}
                />
                {checkPermissions && (
                    <AddCircleBlue
                        type="button"
                        text={uploadBtnText ? uploadBtnText : 'Upload Document'}
                        icon={uploadBtnIcon ? uploadBtnIcon : Images.uploadDockCloud}
                        handleClick={() =>
                            open(
                                <UploadFiles
                                    currentId={resourceId}
                                    onResource={onResource}
                                    selectedTab={selectedTab}
                                    withExpiration={withExpiration}
                                />
                            )
                        }
                    />
                )}
            </div>
            <div className="dock-table-wrapper">
                <FullTable
                    head={filesHead(checkPermissions, noSearch, selectedList, filesList, handleSelect, withExpiration)}
                    body={filesBody(checkPermissions, selectedId, selectedList, handleSelect, reqParams, archived, shareBtn, withExpiration)}
                    loadingType={GET_ACTION_TYPE}
                    list={filesList?.length ? sortListByKey(filesList, 'name') : []}
                    handleClickButton={(name, item) => handleClickActions(name, item)}
                    notYet={fileNotYet}
                    activeRowId={''}
                    height={height}
                    margin={margin ? margin : '0'}
                    selectedList={selectedList}
                />
            </div>
        </div>
    );
};
